import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, Plane, ChevronDown } from 'lucide-react';
import axios from 'axios';
import popularAirportsData from '../../data/popular_airports.json';
import airlinesData from '../../data/airlines.json';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lazy load the full 5,379-airport catalog on first search — mirrors the
// HomePage / DesktopModifySearch pattern instead of a hardcoded 24-airport list.
let fullAirportsData = null;
const loadFullAirports = async () => {
  if (!fullAirportsData) {
    const module = await import('../../data/airports.json');
    fullAirportsData = module.default;
  }
  return fullAirportsData;
};

const INVENTORY_OPTIONAL_FIELDS = [
  'day_of_week',
  'return_date',
  'return_pnr',
  'return_flight_number',
  'return_departure_time',
  'return_arrival_time',
  'return_duration',
  'occasion_label',
  'occasion_label_name',
  'occasion_color',
  'supplier_payment_due_date',
  'ticketing_deadline',
  'release_deadline',
  'internal_remarks',
];

const RETURN_FIELDS = [
  'return_date',
  'return_pnr',
  'return_flight_number',
  'return_departure_time',
  'return_arrival_time',
  'return_duration',
];

const sanitizeInventoryRow = (inv, ticketType) => {
  const row = { ...inv };
  if (ticketType === 'oneway') {
    RETURN_FIELDS.forEach((f) => delete row[f]);
  }
  INVENTORY_OPTIONAL_FIELDS.forEach((f) => {
    if (row[f] === '') delete row[f];
  });
  return row;
};

const formatApiError = (detail, fallback) => {
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const path = (d.loc || []).filter((p) => p !== 'body').join('.');
        return path ? `${path}: ${d.msg}` : d.msg;
      })
      .join('\n');
  }
  if (typeof detail === 'string') return detail;
  return fallback;
};

// Full 927-airline master list, sorted alphabetically by name, instead of
// a hardcoded 7-airline shortlist.
const airlines = [...airlinesData].sort((a, b) => a.name.localeCompare(b.name));

const CreateOfflineFlight = () => {
  const navigate = useNavigate();
  const { flightId } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(flightId && location.pathname.includes('/edit'));
  const [editLoadError, setEditLoadError] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(isEditMode);

  // Occasion labels (active list, loaded once on mount for inventory dropdown)
  const [occasionLabels, setOccasionLabels] = useState([]);
  const [colorHex, setColorHex] = useState({});

  // Step 1: Flight Details
  const [flightDetails, setFlightDetails] = useState({
    airline_code: '',
    airline_name: '',
    flight_number: '',
    route: {
      from_airport: '',
      to_airport: '',
      from_city: '',
      to_city: ''
    },
    ticket_type: 'oneway',
    flight_type: 'direct',
    departure_time: '',
    arrival_time: '',
    duration: '',
    segments: [],
    cabin_baggage: '7 Kg',
    checkin_baggage: '15 Kg',
    meal_included: false,
    class_type: 'Economy',
    fare_type: 'Regular',
    refundable: false,
    refund_amount: '',
    journey_scope: ''
  });

  // Step 2: Schedule & Inventory
  const [schedule, setSchedule] = useState({
    schedule_type: 'specific_dates',
    valid_from: '',
    valid_to: '',
    days_of_week: [],
    inventory: []
  });

  // Step 3: Pricing
  const [fares, setFares] = useState({
    adult: '',
    child: '',
    infant: ''
  });

  // Step 3b: Fare breakdown (for GST invoicing). The total (`adult` field
  // above) is what the customer pays; the breakdown below is what ACF
  // received as pass-through from the airline. Enter once per flight — it
  // flows to every booking made against it.
  const [fareBreakdown, setFareBreakdown] = useState({
    base_fare:      '',
    yq_fuel:        '',
    yr_misc:        '',
    k3_gst:         '',
    other_charges:  '',
  });

  const handleAirlineChange = (code) => {
    const airline = airlines.find(a => a.code === code);
    setFlightDetails({
      ...flightDetails,
      airline_code: code,
      airline_name: airline?.name || ''
    });
  };

  const lookupAirport = (code) => {
    if (!code) return undefined;
    if (fullAirportsData) {
      const hit = fullAirportsData.find((a) => a.code === code);
      if (hit) return hit;
    }
    return popularAirportsData.find((a) => a.code === code);
  };

  const handleFromAirportChange = (code) => {
    const airport = lookupAirport(code);
    setFlightDetails({
      ...flightDetails,
      route: {
        ...flightDetails.route,
        from_airport: code,
        from_city: airport?.city || ''
      }
    });
  };

  const handleToAirportChange = (code) => {
    const airport = lookupAirport(code);
    setFlightDetails({
      ...flightDetails,
      route: {
        ...flightDetails.route,
        to_airport: code,
        to_city: airport?.city || ''
      }
    });
  };

  const addSegment = () => {
    setFlightDetails({
      ...flightDetails,
      segments: [
        ...flightDetails.segments,
        {
          from_airport: '',
          to_airport: '',
          from_city: '',
          to_city: '',
          departure_time: '',
          arrival_time: '',
          duration: '',
          layover: ''
        }
      ]
    });
  };

  const removeSegment = (index) => {
    const newSegments = flightDetails.segments.filter((_, i) => i !== index);
    setFlightDetails({ ...flightDetails, segments: newSegments });
  };

  const updateSegment = (index, field, value) => {
    const newSegments = [...flightDetails.segments];
    newSegments[index] = { ...newSegments[index], [field]: value };
    setFlightDetails({ ...flightDetails, segments: newSegments });
  };

  const addInventoryDate = () => {
    const isRT = flightDetails.ticket_type === 'roundtrip';
    setSchedule({
      ...schedule,
      inventory: [
        ...schedule.inventory,
        {
          date: '',
          pnr: '',
          total_seats: 50,
          sold_seats: 0,
          available_seats: 50,
          status: 'active',
          // RT cycle fields — only relevant when ticket_type === 'roundtrip'
          ...(isRT ? {
            return_date: '',
            return_pnr: '',
            return_flight_number: '',
            return_departure_time: '',
            return_arrival_time: '',
            return_duration: '',
          } : {}),
          // Occasion (optional for both OW & RT)
          occasion_label: '',
          occasion_label_name: '',
          occasion_color: '',
        }
      ]
    });
  };

  const removeInventoryDate = (index) => {
    const newInventory = schedule.inventory.filter((_, i) => i !== index);
    setSchedule({ ...schedule, inventory: newInventory });
  };

  const updateInventory = (index, field, value) => {
    const newInventory = [...schedule.inventory];
    newInventory[index] = { ...newInventory[index], [field]: value };
    
    // Auto-calculate available seats
    if (field === 'total_seats') {
      newInventory[index].available_seats = value - newInventory[index].sold_seats;
    }
    
    setSchedule({ ...schedule, inventory: newInventory });
  };

  const toggleDayOfWeek = (day) => {
    const days = schedule.days_of_week.includes(day)
      ? schedule.days_of_week.filter(d => d !== day)
      : [...schedule.days_of_week, day];
    setSchedule({ ...schedule, days_of_week: days });
  };

  useEffect(() => {
    if (!isEditMode || !flightId) {
      setInitializing(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/offline-flights/${flightId}`, { withCredentials: true });
        if (cancelled || !res.data?.success) return;
        const f = res.data.data;
        setFlightDetails({
          airline_code: f.airline_code || '',
          airline_name: f.airline_name || '',
          flight_number: f.flight_number || '',
          route: {
            from_airport: f.route?.from_airport || '',
            to_airport: f.route?.to_airport || '',
            from_city: f.route?.from_city || '',
            to_city: f.route?.to_city || ''
          },
          ticket_type: f.ticket_type || 'oneway',
          flight_type: f.flight_type || 'direct',
          departure_time: f.departure_time || '',
          arrival_time: f.arrival_time || '',
          duration: f.duration || '',
          segments: f.segments || [],
          cabin_baggage: f.cabin_baggage || '7 Kg',
          checkin_baggage: f.checkin_baggage || '15 Kg',
          meal_included: !!f.meal_included,
          class_type: f.class_type || 'Economy',
          fare_type: f.fare_type || 'Regular',
          refundable: !!f.refundable,
          refund_amount: f.refund_amount || '',
          journey_scope: f.journey_scope || ''
        });
        setSchedule({
          schedule_type: f.schedule_type || 'specific_dates',
          valid_from: f.valid_from || '',
          valid_to: f.valid_to || '',
          days_of_week: f.days_of_week || [],
          inventory: (f.inventory || []).map((inv) => {
            const base = {
              date: inv.date,
              pnr: inv.pnr,
              total_seats: inv.total_seats,
              sold_seats: inv.sold_seats ?? 0,
              available_seats: inv.available_seats,
              status: inv.status || 'active',
              held_seats: inv.held_seats ?? 0,
              agent_blocked_seats: inv.agent_blocked_seats ?? 0,
              occasion_label: inv.occasion_label || '',
              occasion_label_name: inv.occasion_label_name || '',
              occasion_color: inv.occasion_color || '',
            };
            // Surface RT cycle fields only when the parent is a RT flight.
            if ((f.ticket_type || 'oneway') === 'roundtrip') {
              return {
                ...base,
                return_date: inv.return_date || '',
                return_pnr: inv.return_pnr || '',
                return_flight_number: inv.return_flight_number || '',
                return_departure_time: inv.return_departure_time || '',
                return_arrival_time: inv.return_arrival_time || '',
                return_duration: inv.return_duration || '',
              };
            }
            return base;
          })
        });
        if (f.fares) {
          setFares({
            adult: String(f.fares.adult ?? ''),
            child: String(f.fares.child ?? ''),
            infant: String(f.fares.infant ?? '')
          });
        }
        const fb = f.fare_breakdown || {};
        setFareBreakdown({
          base_fare: fb.base_fare != null ? String(fb.base_fare) : '',
          yq_fuel: fb.yq_fuel != null ? String(fb.yq_fuel) : '',
          yr_misc: fb.yr_misc != null ? String(fb.yr_misc) : '',
          k3_gst: fb.k3_gst != null ? String(fb.k3_gst) : '',
          other_charges: fb.other_charges != null ? String(fb.other_charges) : ''
        });
      } catch (e) {
        console.error(e);
        setEditLoadError(true);
        alert('Failed to load flight for editing');
        navigate('/admin/offline-inventory');
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, flightId, navigate]);

  // Load active occasion labels once, for the inventory <Select>
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/occasion-labels`);
        if (cancelled) return;
        setOccasionLabels(res.data?.labels || []);
        setColorHex(res.data?.color_hex || {});
      } catch (e) {
        // Non-fatal — admin can still create rows without a label
        console.warn('Could not load occasion labels:', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // When ticket_type flips, scrub stale return_* fields from existing rows
  // (RT → OW must drop return_*; OW → RT must initialise empty return_* fields).
  const handleTicketTypeChange = (newType) => {
    setFlightDetails((prev) => ({ ...prev, ticket_type: newType }));
    setSchedule((prev) => ({
      ...prev,
      inventory: prev.inventory.map((inv) => {
        if (newType === 'oneway') {
          // strip all return_* fields
          const {
            return_date, return_pnr, return_flight_number,
            return_departure_time, return_arrival_time, return_duration,
            ...rest
          } = inv;
          return rest;
        }
        // roundtrip: ensure return_* placeholders exist
        return {
          ...inv,
          return_date: inv.return_date || '',
          return_pnr: inv.return_pnr || '',
          return_flight_number: inv.return_flight_number || '',
          return_departure_time: inv.return_departure_time || '',
          return_arrival_time: inv.return_arrival_time || '',
          return_duration: inv.return_duration || '',
        };
      }),
    }));
  };

  // Apply an occasion label to a given inventory row — denormalises name + color.
  const applyOccasion = (index, slug) => {
    const newInventory = [...schedule.inventory];
    if (!slug) {
      newInventory[index] = {
        ...newInventory[index],
        occasion_label: '',
        occasion_label_name: '',
        occasion_color: '',
      };
    } else {
      const lab = occasionLabels.find((l) => l.slug === slug);
      newInventory[index] = {
        ...newInventory[index],
        occasion_label: slug,
        occasion_label_name: lab?.name || '',
        occasion_color: lab?.color || '',
      };
    }
    setSchedule({ ...schedule, inventory: newInventory });
  };

  const handleSubmit = async () => {
    // Validation
    if (!flightDetails.airline_code || !flightDetails.flight_number) {
      alert('Please fill all required fields');
      return;
    }

    if (!fares.adult || !fares.child || !fares.infant) {
      alert('Please set all fare prices');
      return;
    }

    if (schedule.inventory.length === 0) {
      alert('Please add at least one inventory date');
      return;
    }

    // RT pairing client guard — mirrors the backend validator so the admin
    // gets a clear inline error rather than a generic 422 from the API.
    if (flightDetails.ticket_type === 'roundtrip') {
      for (let i = 0; i < schedule.inventory.length; i++) {
        const inv = schedule.inventory[i];
        if (!inv.return_date || !inv.return_pnr) {
          alert(`Cycle ${i + 1}: Round-trip flights require both Return Date and Return PNR.`);
          return;
        }
        if (inv.return_date <= inv.date) {
          alert(`Cycle ${i + 1}: Return Date (${inv.return_date}) must be strictly after Onward Date (${inv.date}).`);
          return;
        }
      }
    }

    setLoading(true);

    try {
      const payload = {
        ...flightDetails,
        ...schedule,
        inventory: schedule.inventory.map((inv) =>
          sanitizeInventoryRow(inv, flightDetails.ticket_type)
        ),
        fares: {
          adult: parseFloat(fares.adult),
          child: parseFloat(fares.child),
          infant: parseFloat(fares.infant)
        },
        fare_breakdown: {
          base_fare:     parseFloat(fareBreakdown.base_fare)     || 0,
          yq_fuel:       parseFloat(fareBreakdown.yq_fuel)       || 0,
          yr_misc:       parseFloat(fareBreakdown.yr_misc)       || 0,
          k3_gst:        parseFloat(fareBreakdown.k3_gst)        || 0,
          other_charges: parseFloat(fareBreakdown.other_charges) || 0,
        }
      };
      if (!payload.journey_scope) {
        delete payload.journey_scope;
      }

      const cfg = { withCredentials: true };
      const response = isEditMode
        ? await axios.put(`${API}/admin/offline-flights/${flightId}`, payload, cfg)
        : await axios.post(`${API}/admin/offline-flights`, payload, cfg);

      if (response.data.success) {
        alert(isEditMode ? 'Offline flight updated successfully!' : 'Offline flight created successfully!');
        navigate('/admin/offline-inventory');
      }
    } catch (error) {
      console.error('Error creating flight:', error);
      alert(formatApiError(
        error.response?.data?.detail,
        isEditMode ? 'Failed to update flight' : 'Failed to create flight'
      ));
    } finally {
      setLoading(false);
    }
  };

  // Searchable airport picker — shows the 49 popular airports first, then
  // lazy-loads the full 5,379-airport catalog on the first keystroke, so the
  // admin can select any global airport just like the HomePage / search page.
  const AirportPicker = ({ label, selectedCode, selectedCity, onSelect }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [allAirports, setAllAirports] = useState(null);
    const inputRef = useRef(null);
    const ref = useRef(null);

    const searchCatalog = allAirports || popularAirportsData;
    const info = searchCatalog.find((a) => a.code === selectedCode) ||
                 popularAirportsData.find((a) => a.code === selectedCode);

    const handleSearchChange = async (value) => {
      setSearch(value);
      if (value.length > 0 && !allAirports) {
        setIsSearching(true);
        try {
          const fullData = await loadFullAirports();
          setAllAirports(fullData);
        } catch (e) {
          // Silent — the popular list stays as the fallback catalog.
        } finally {
          setIsSearching(false);
        }
      }
    };

    const filtered = useMemo(() => {
      if (!search) return popularAirportsData.slice(0, 50);
      const q = search.toLowerCase();
      return searchCatalog
        .filter(
          (a) =>
            a.city.toLowerCase().includes(q) ||
            a.code.toLowerCase().includes(q) ||
            a.name.toLowerCase().includes(q)
        )
        .slice(0, 100);
    }, [search, searchCatalog]);

    useEffect(() => {
      if (open) setTimeout(() => inputRef.current?.focus(), 50);
    }, [open]);

    useEffect(() => {
      const handler = (e) => {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
      <div ref={ref} className="relative">
        <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
        <button
          type="button"
          onClick={() => { setOpen(!open); setSearch(''); }}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B] text-left flex items-center justify-between bg-white"
          data-testid={`offline-airport-${label.toLowerCase().includes('from') ? 'from' : 'to'}`}
        >
          <span className={selectedCode ? 'text-gray-900' : 'text-gray-400'}>
            {selectedCode ? `${selectedCode} — ${info?.city || selectedCity || ''}` : 'Select Airport'}
          </span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute z-50 top-full left-0 mt-1 w-full min-w-[280px] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            <div className="p-2 border-b border-gray-100">
              <input
                ref={inputRef}
                type="text"
                placeholder="City, airport or code..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-[#659A9B]"
              />
              {isSearching && <p className="text-xs text-gray-400 mt-1">Searching all airports…</p>}
              {!search && !isSearching && (
                <p className="text-xs text-gray-400 mt-1">Popular airports — type to search all {`5,379`}</p>
              )}
            </div>
            <div className="max-h-56 overflow-y-auto">
              {filtered.map((a) => (
                <button
                  type="button"
                  key={a.code}
                  onClick={() => { onSelect(a.code); setOpen(false); setSearch(''); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${a.code === selectedCode ? 'bg-[#659A9B]/10' : ''}`}
                >
                  <span className="font-semibold text-[#1B375B]">{a.code}</span> — {a.city}, {a.country}
                  <span className="block text-xs text-gray-400">{a.name}</span>
                </button>
              ))}
              {filtered.length === 0 && <p className="text-center py-3 text-sm text-gray-400">No airports found</p>}
            </div>
          </div>
        )}
      </div>
    );
  };

  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);

  if (initializing) {
    return (
      <div className="p-12 text-center">
        <div className="inline-block w-10 h-10 border-4 border-[#659A9B] border-t-transparent rounded-full animate-spin" />
        <p className="mt-4 text-gray-600">Loading flight…</p>
      </div>
    );
  }

  if (editLoadError) {
    return null;
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <button
            onClick={() => navigate('/admin/offline-inventory')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#1B375B] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-[#1B375B]">
            {isEditMode ? 'Edit Offline Flight' : 'Create Offline Flight'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEditMode ? 'Update block ticket inventory' : 'Add a new block ticket to inventory'}
          </p>

          {/* Progress Indicator */}
          <div className="flex items-center gap-4 mt-6">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step >= s ? 'bg-[#659A9B] text-white' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step > s ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 3 && (
                  <div className={`flex-1 h-1 mx-2 ${step > s ? 'bg-[#659A9B]' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-sm">
            <span className={step >= 1 ? 'text-[#659A9B] font-semibold' : 'text-gray-500'}>Flight Details</span>
            <span className={step >= 2 ? 'text-[#659A9B] font-semibold' : 'text-gray-500'}>Schedule & Inventory</span>
            <span className={step >= 3 ? 'text-[#659A9B] font-semibold' : 'text-gray-500'}>Pricing</span>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Flight Details</h2>

              {/* Airline & Flight Number */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Airline *</label>
                  <select
                    value={flightDetails.airline_code}
                    onChange={(e) => handleAirlineChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                    required
                  >
                    <option value="">Select Airline</option>
                    {airlines.map((a) => (
                      <option key={a.code} value={a.code}>{a.code} - {a.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Flight Number *</label>
                  <input
                    type="text"
                    placeholder="e.g., 6E-2345"
                    value={flightDetails.flight_number}
                    onChange={(e) => setFlightDetails({ ...flightDetails, flight_number: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                    required
                  />
                </div>
              </div>

              {/* Route */}
              <div className="grid grid-cols-2 gap-4">
                <AirportPicker
                  label="From Airport *"
                  selectedCode={flightDetails.route.from_airport}
                  selectedCity={flightDetails.route.from_city}
                  onSelect={handleFromAirportChange}
                />
                <AirportPicker
                  label="To Airport *"
                  selectedCode={flightDetails.route.to_airport}
                  selectedCity={flightDetails.route.to_city}
                  onSelect={handleToAirportChange}
                />
              </div>

              {/* Ticket & Flight Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Ticket Type</label>
                  <select
                    value={flightDetails.ticket_type}
                    onChange={(e) => handleTicketTypeChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                  >
                    <option value="oneway">One-way</option>
                    <option value="roundtrip">Round-trip</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Flight Type</label>
                  <select
                    value={flightDetails.flight_type}
                    onChange={(e) => setFlightDetails({ ...flightDetails, flight_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                  >
                    <option value="direct">Direct (Non-stop)</option>
                    <option value="via">Via (Connecting)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Journey scope</label>
                  <select
                    value={flightDetails.journey_scope || ''}
                    onChange={(e) => setFlightDetails({ ...flightDetails, journey_scope: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                  >
                    <option value="">Not set</option>
                    <option value="domestic">Domestic</option>
                    <option value="international">International</option>
                  </select>
                </div>
              </div>

              {/* Direct Flight Timing */}
              {flightDetails.flight_type === 'direct' && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Departure Time *</label>
                    <input
                      type="time"
                      value={flightDetails.departure_time}
                      onChange={(e) => setFlightDetails({ ...flightDetails, departure_time: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Arrival Time *</label>
                    <input
                      type="time"
                      value={flightDetails.arrival_time}
                      onChange={(e) => setFlightDetails({ ...flightDetails, arrival_time: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Duration *</label>
                    <input
                      type="text"
                      placeholder="e.g., 2h 15m"
                      value={flightDetails.duration}
                      onChange={(e) => setFlightDetails({ ...flightDetails, duration: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Via Flight Segments */}
              {flightDetails.flight_type === 'via' && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-gray-900">Flight Segments</h3>
                    <button
                      onClick={addSegment}
                      className="flex items-center gap-2 px-3 py-1 bg-[#659A9B] text-white text-sm rounded-lg hover:bg-[#1B375B]"
                    >
                      <Plus className="w-4 h-4" />
                      Add Segment
                    </button>
                  </div>

                  {flightDetails.segments.map((segment, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-semibold text-sm">Segment {index + 1}</h4>
                        <button
                          onClick={() => removeSegment(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="From Airport"
                          value={segment.from_airport}
                          onChange={(e) => updateSegment(index, 'from_airport', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <input
                          type="text"
                          placeholder="To Airport"
                          value={segment.to_airport}
                          onChange={(e) => updateSegment(index, 'to_airport', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <input
                          type="time"
                          placeholder="Departure"
                          value={segment.departure_time}
                          onChange={(e) => updateSegment(index, 'departure_time', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <input
                          type="time"
                          placeholder="Arrival"
                          value={segment.arrival_time}
                          onChange={(e) => updateSegment(index, 'arrival_time', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Duration (e.g., 1h 30m)"
                          value={segment.duration}
                          onChange={(e) => updateSegment(index, 'duration', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Layover (optional)"
                          value={segment.layover}
                          onChange={(e) => updateSegment(index, 'layover', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Amenities */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cabin Baggage</label>
                  <input
                    type="text"
                    value={flightDetails.cabin_baggage}
                    onChange={(e) => setFlightDetails({ ...flightDetails, cabin_baggage: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Check-in Baggage</label>
                  <input
                    type="text"
                    value={flightDetails.checkin_baggage}
                    onChange={(e) => setFlightDetails({ ...flightDetails, checkin_baggage: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Class</label>
                  <select
                    value={flightDetails.class_type}
                    onChange={(e) => setFlightDetails({ ...flightDetails, class_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                  >
                    <option value="Economy">Economy</option>
                    <option value="Business">Business</option>
                    <option value="First">First</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Fare Type</label>
                  <select
                    value={flightDetails.fare_type}
                    onChange={(e) => setFlightDetails({ ...flightDetails, fare_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                  >
                    <option value="Regular">Regular</option>
                    <option value="Flexi">Flexi</option>
                    <option value="Flexi Plus">Flexi Plus</option>
                  </select>
                </div>
                <div className="flex items-center pt-8">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={flightDetails.meal_included}
                      onChange={(e) => setFlightDetails({ ...flightDetails, meal_included: e.target.checked })}
                      className="w-5 h-5 text-[#659A9B] rounded focus:ring-2 focus:ring-[#659A9B]"
                    />
                    <span className="text-sm font-semibold text-gray-700">Meal Included</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={flightDetails.refundable}
                      onChange={(e) => setFlightDetails({ ...flightDetails, refundable: e.target.checked })}
                      className="w-5 h-5 text-[#659A9B] rounded focus:ring-2 focus:ring-[#659A9B]"
                    />
                    <span className="text-sm font-semibold text-gray-700">Refundable</span>
                  </label>
                </div>
                {flightDetails.refundable && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Refund Amount</label>
                    <input
                      type="text"
                      placeholder="e.g., ₹500 or 100% or 50%"
                      value={flightDetails.refund_amount}
                      onChange={(e) => setFlightDetails({ ...flightDetails, refund_amount: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Schedule & Inventory</h2>

              {/* Schedule Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Schedule Type</label>
                <select
                  value={schedule.schedule_type}
                  onChange={(e) => setSchedule({ ...schedule, schedule_type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                >
                  <option value="once">One-time (Specific Date)</option>
                  <option value="weekly">Weekly Schedule</option>
                  <option value="specific_dates">Specific Dates</option>
                </select>
              </div>

              {/* Weekly Schedule */}
              {schedule.schedule_type === 'weekly' && (
                <div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Valid From</label>
                      <input
                        type="date"
                        value={schedule.valid_from}
                        onChange={(e) => setSchedule({ ...schedule, valid_from: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Valid To</label>
                      <input
                        type="date"
                        value={schedule.valid_to}
                        onChange={(e) => setSchedule({ ...schedule, valid_to: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                      />
                    </div>
                  </div>

                  <label className="block text-sm font-semibold text-gray-700 mb-2">Select Days</label>
                  <div className="grid grid-cols-4 gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                      <label key={day} className="flex items-center gap-2 cursor-pointer p-3 border border-gray-300 rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={schedule.days_of_week.includes(day)}
                          onChange={() => toggleDayOfWeek(day)}
                          className="w-5 h-5 text-[#659A9B] rounded focus:ring-2 focus:ring-[#659A9B]"
                        />
                        <span className="text-sm">{day.substring(0, 3)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Inventory Dates */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-semibold text-gray-700">Inventory Dates</label>
                  <button
                    onClick={addInventoryDate}
                    className="flex items-center gap-2 px-3 py-1 bg-[#659A9B] text-white text-sm rounded-lg hover:bg-[#1B375B]"
                  >
                    <Plus className="w-4 h-4" />
                    Add Date
                  </button>
                </div>

                {schedule.inventory.map((inv, index) => {
                  const isRT = flightDetails.ticket_type === 'roundtrip';
                  const swatch = inv.occasion_color ? (colorHex[inv.occasion_color] || '#659A9B') : null;
                  return (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 mb-3 bg-gray-50"
                    data-testid={`inventory-row-${index}`}
                  >
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-sm">
                        {isRT ? `Cycle ${index + 1} (Onward + Return)` : `Date ${index + 1}`}
                      </h4>
                      <button
                        onClick={() => removeInventoryDate(index)}
                        className="text-red-600 hover:text-red-800"
                        data-testid={`inventory-remove-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Onward leg */}
                    {isRT && (
                      <div className="text-xs font-semibold text-[#1B375B] uppercase tracking-wide mb-2">
                        Onward · {flightDetails.route.from_airport || '—'} → {flightDetails.route.to_airport || '—'}
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{isRT ? 'Onward Date' : 'Date'}</label>
                        <input
                          type="date"
                          value={inv.date}
                          onChange={(e) => updateInventory(index, 'date', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          data-testid={`inv-${index}-date`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">{isRT ? 'Onward PNR' : 'PNR'}</label>
                        <input
                          type="text"
                          placeholder="e.g., PNR001"
                          value={inv.pnr}
                          onChange={(e) => updateInventory(index, 'pnr', e.target.value.toUpperCase())}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          data-testid={`inv-${index}-pnr`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Total Seats</label>
                        <input
                          type="number"
                          min="1"
                          value={inv.total_seats}
                          onChange={(e) => updateInventory(index, 'total_seats', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          data-testid={`inv-${index}-seats`}
                        />
                      </div>
                    </div>

                    {/* Return leg — only when RT */}
                    {isRT && (
                      <div className="mt-4 pt-4 border-t border-dashed border-gray-300">
                        <div className="text-xs font-semibold text-[#1B375B] uppercase tracking-wide mb-2">
                          Return · {flightDetails.route.to_airport || '—'} → {flightDetails.route.from_airport || '—'}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Return Date</label>
                            <input
                              type="date"
                              value={inv.return_date || ''}
                              onChange={(e) => updateInventory(index, 'return_date', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              data-testid={`inv-${index}-return-date`}
                              min={inv.date || undefined}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Return PNR</label>
                            <input
                              type="text"
                              placeholder="e.g., PNR001R"
                              value={inv.return_pnr || ''}
                              onChange={(e) => updateInventory(index, 'return_pnr', e.target.value.toUpperCase())}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              data-testid={`inv-${index}-return-pnr`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Return Flight #</label>
                            <input
                              type="text"
                              placeholder="e.g., EK-506"
                              value={inv.return_flight_number || ''}
                              onChange={(e) => updateInventory(index, 'return_flight_number', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              data-testid={`inv-${index}-return-flight`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Return Departure</label>
                            <input
                              type="time"
                              value={inv.return_departure_time || ''}
                              onChange={(e) => updateInventory(index, 'return_departure_time', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              data-testid={`inv-${index}-return-dep`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Return Arrival</label>
                            <input
                              type="time"
                              value={inv.return_arrival_time || ''}
                              onChange={(e) => updateInventory(index, 'return_arrival_time', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              data-testid={`inv-${index}-return-arr`}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Return Duration</label>
                            <input
                              type="text"
                              placeholder="e.g., 4h 45m"
                              value={inv.return_duration || ''}
                              onChange={(e) => updateInventory(index, 'return_duration', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              data-testid={`inv-${index}-return-dur`}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Occasion label — applies to both OW & RT */}
                    <div className="mt-4 pt-4 border-t border-dashed border-gray-300">
                      <label className="block text-xs text-gray-600 mb-1">Occasion Label (optional)</label>
                      <div className="flex items-center gap-3">
                        <select
                          value={inv.occasion_label || ''}
                          onChange={(e) => applyOccasion(index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          data-testid={`inv-${index}-occasion`}
                        >
                          <option value="">— None —</option>
                          {occasionLabels.map((lab) => (
                            <option key={lab.slug} value={lab.slug}>
                              {lab.name} ({lab.slug})
                            </option>
                          ))}
                        </select>
                        {swatch && (
                          <span
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold text-white whitespace-nowrap"
                            style={{ backgroundColor: swatch }}
                          >
                            {inv.occasion_label_name || inv.occasion_label}
                          </span>
                        )}
                      </div>
                      {occasionLabels.length === 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          No active labels yet. <a href="/admin/occasion-labels" className="underline text-[#1B375B]">Manage labels →</a>
                        </p>
                      )}
                    </div>
                  </div>
                );
                })}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Pricing</h2>
              <p className="text-sm text-gray-600 mb-6">Set direct selling prices (no markup applied)</p>

              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Adult Fare (₹) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={fares.adult}
                    onChange={(e) => setFares({ ...fares, adult: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                    placeholder="e.g., 5500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Child Fare (₹) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={fares.child}
                    onChange={(e) => setFares({ ...fares, child: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                    placeholder="e.g., 4200"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Infant Fare (₹) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={fares.infant}
                    onChange={(e) => setFares({ ...fares, infant: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B]"
                    placeholder="e.g., 1500"
                    required
                  />
                </div>
              </div>

              {/* Fare Breakdown (GST invoicing) */}
              <div className="mt-8 border-t pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-1">Fare Breakdown (for GST invoicing)</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Enter how the airline bills you (pass-through). Leave blank if unknown — but GST invoices
                  cannot be raised for bookings on this flight if breakdown is zero.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { key: 'base_fare',    label: 'Base Fare (BF)' },
                    { key: 'yq_fuel',      label: 'Fuel Surcharge (YQ)' },
                    { key: 'yr_misc',      label: 'Carrier Misc (YR)' },
                    { key: 'k3_gst',       label: 'Airline GST (K3)' },
                    { key: 'other_charges', label: 'Other Charges' },
                  ].map((f) => (
                    <div key={f.key}>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">{f.label} ₹</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={fareBreakdown[f.key]}
                        onChange={(e) => setFareBreakdown({ ...fareBreakdown, [f.key]: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        placeholder="0"
                        data-testid={`offline-fare-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex justify-between text-sm bg-gray-50 rounded px-3 py-2">
                  <span className="text-gray-600">Supplier subtotal (pass-through):</span>
                  <span className="font-semibold">
                    ₹{(
                      (parseFloat(fareBreakdown.base_fare)     || 0) +
                      (parseFloat(fareBreakdown.yq_fuel)       || 0) +
                      (parseFloat(fareBreakdown.yr_misc)       || 0) +
                      (parseFloat(fareBreakdown.k3_gst)        || 0) +
                      (parseFloat(fareBreakdown.other_charges) || 0)
                    ).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              {/* Summary */}
              <div className="mt-8 border-t pt-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Summary</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Flight:</span>
                    <span className="font-semibold">{flightDetails.airline_name} {flightDetails.flight_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Route:</span>
                    <span className="font-semibold">{flightDetails.route.from_airport} → {flightDetails.route.to_airport}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Schedule:</span>
                    <span className="font-semibold capitalize">{schedule.schedule_type} ({schedule.inventory.length} dates)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Inventory:</span>
                    <span className="font-semibold">{schedule.inventory.reduce((sum, inv) => sum + inv.total_seats, 0)} seats</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <button
              onClick={prevStep}
              disabled={step === 1}
              className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4" />
              Previous
            </button>

            {step < 3 ? (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-2 bg-[#659A9B] hover:bg-[#1B375B] text-white rounded-lg transition-colors"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Create Flight
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOfflineFlight;
